import { gql } from "@apollo/client";
import makeStyles from "../../../makeStylesV4Compat";
import {
  CorrectionCostToMoveTopBar_compStructure as CompStructure,
  CorrectionCostToMoveTopBar_department as Department,
  CorrectionCostToMoveTopBar_employee as Employee,
  CorrectionCostToMoveTopBar_market as Market,
} from "src/__generated__/graphql";
import CorrectionCostToMoveFilterBar from "./CorrectionCostToMoveFilterBar";
import {
  CorrectionCostToMoveFilterParam,
  FilterOptionsType,
} from "./CorrectionCostToMoveFilters";
import { CorrectionCostToMoveTargetSelector } from "./CorrectionCostToMoveTargetSelector";

const useStyles = makeStyles(() => ({
  filterAndSelector: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type Props = {
  compStructure: CompStructure | null;
  managers: Employee[];
  departments: Department[];
  markets: Market[];
  filter: { [key: string]: number[] | null | undefined };
  setFilter: (field: string, values: number[]) => void;
  resetFilters: () => void;
  targetBandPoint: string | null;
  setTargetBandPoint: (value: string) => void;
};

export function CorrectionCostToMoveTopBar({
  compStructure,
  managers,
  departments,
  markets,
  filter,
  setFilter,
  resetFilters,
  targetBandPoint,
  setTargetBandPoint,
}: Props): JSX.Element {
  const classes = useStyles();

  const filterOptions: FilterOptionsType = {
    [CorrectionCostToMoveFilterParam.MANAGER]: managers,
    [CorrectionCostToMoveFilterParam.REPORTS]: [],
    [CorrectionCostToMoveFilterParam.CURRENT_DEPARTMENT]: departments,
    [CorrectionCostToMoveFilterParam.CURRENT_LADDER]: departments.flatMap(
      (dep) => dep.ladders
    ),
    [CorrectionCostToMoveFilterParam.CURRENT_LEVEL]:
      compStructure?.levels ?? [],
    [CorrectionCostToMoveFilterParam.LOCATION_GROUP]: markets.flatMap(
      (market) => market.locationGroups
    ),
    [CorrectionCostToMoveFilterParam.CURRENT_BAND_POSITION]: [],
  };
  return (
    <div className={classes.filterAndSelector}>
      <CorrectionCostToMoveFilterBar
        onChange={setFilter}
        onReset={resetFilters}
        filter={filter}
        filterOptions={filterOptions}
      />
      <CorrectionCostToMoveTargetSelector
        compStructure={compStructure}
        targetBandPoint={targetBandPoint}
        setTargetBandPoint={setTargetBandPoint}
      />
    </div>
  );
}

CorrectionCostToMoveTopBar.fragments = {
  compStructure: gql`
    ${CorrectionCostToMoveTargetSelector.fragments.compStructure}
    fragment CorrectionCostToMoveTopBar_compStructure on CompStructure {
      id
      levels
      ...CorrectionCostToMoveTargetSelector_compStructure
    }
  `,
  employee: gql`
    fragment CorrectionCostToMoveTopBar_employee on Employee {
      id
      displayName
    }
  `,
  department: gql`
    fragment CorrectionCostToMoveTopBar_department on Department {
      id
      name
      ladders {
        id
        name
      }
    }
  `,
  market: gql`
    fragment CorrectionCostToMoveTopBar_market on Market {
      id
      name
      locationGroups {
        id
        name
      }
    }
  `,
};
